import { Branding } from './types';

/**
 * Tenant theme — the wire between the studio's Appearance choice and the
 * design-token layer every FitBOXX app renders through.
 *
 * The whole design language resolves to CSS custom properties, so re-skinning
 * a studio is one move: publish a different token block. The SERVER resolves
 * the block (preset + the studio's overrides + computed readable ink, see
 * `tenants/appearance.py`) and ships it on `branding.theme`, so the admin
 * app, the member portal and the public booking widget can never drift from
 * each other by re-implementing the resolution three times.
 *
 * Why a <style> tag rather than `style.setProperty` on <html>: an inline
 * custom property on the root element wins over every stylesheet rule,
 * including the `@layer base` defaults in each app's index.css and any future
 * dark-mode re-definitions. A tag appended last wins by source order instead,
 * which leaves those layers able to do their job.
 *
 * `--fb-*` below are the older widget-facing variables and are kept: they are
 * the booking widget's own vocabulary and mean something narrower (a logo
 * tint). Overloading them would have re-skinned the widget the first time
 * anyone touched the admin theme.
 */

const STYLE_ID = 'fitboxx-theme';
const CACHE_KEY = 'fitboxx.theme.tokens';

/**
 * Which app is painting. The studio's Appearance screen has a
 * `member_portal_follows` switch; honouring it needs to know whether the
 * document being themed is the member portal or the back office, and the
 * shared layer cannot tell on its own. Each entry point declares itself.
 *
 * Defaults to 'admin' so an app that forgets to call this still gets its
 * theme -- the failure mode of a missing declaration should be "themed",
 * not "silently unbranded".
 */
export type ThemeScope = 'admin' | 'member' | 'widget';
let themeScope: ThemeScope = 'admin';

/** Declare which app this document is. Call once, before the first paint. */
export function setThemeScope(scope: ThemeScope): void {
  themeScope = scope;
}

/** The house default — the cream/sage look FitBOXX ships with. Mirrors
 *  `tenants.appearance.PRESETS['wellness-cream']`; it is only ever used for
 *  the first paint before the server answers, and the server then re-asserts. */
export const DEFAULT_THEME_PRESET = 'wellness-cream';

export interface ResolvedTheme {
  preset: string;
  preset_name?: string;
  family?: 'light' | 'dark' | string;
  tokens: Record<string, string>;
  overrides?: Record<string, string>;
  radius?: string;
  appearance?: {
    density?: string;
    radius?: string;
    sidebar_tinted?: boolean;
    member_portal_follows?: boolean;
  };
}

const DENSITY_SCALE: Record<string, string> = {
  comfortable: '1',
  cosy: '0.92',
  compact: '0.84',
};

function cssFor(theme: ResolvedTheme): string {
  const lines = Object.entries(theme.tokens || {})
    .map(([key, value]) => `--${key}: ${value};`);
  if (theme.radius) lines.push(`--radius: ${theme.radius};`);
  const density = DENSITY_SCALE[theme.appearance?.density || 'comfortable'] || '1';
  lines.push(`--density-scale: ${density};`);
  if (theme.appearance?.sidebar_tinted) {
    // A tinted sidebar is the accent wash, not a second colour to pick —
    // deriving it from the primary means it can never clash with the theme
    // the studio actually chose.
    lines.push('--sidebar: var(--accent);');
  }
  return `:root { ${lines.join(' ')} }`;
}

/** Publish (or clear, with null) the tenant theme on the document. */
export function applyTheme(theme: ResolvedTheme | null): void {
  if (typeof document === 'undefined') return;
  const existing = document.getElementById(STYLE_ID);
  if (!theme || !theme.tokens || !Object.keys(theme.tokens).length) {
    existing?.remove();
    return;
  }
  // The studio said the member portal does NOT follow the back office
  // theme. Explicit false only: an older payload with no `appearance` block
  // predates the switch and must keep behaving as it always did.
  if (themeScope === 'member' && theme.appearance?.member_portal_follows === false) {
    existing?.remove();
    return;
  }
  const css = cssFor(theme);
  if (existing) {
    existing.textContent = css;
    return;
  }
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = css;
  // Appended to <head> last so it wins by source order over the app's own
  // @layer base block without needing !important anywhere.
  document.head.appendChild(el);
}

function cacheTheme(theme: ResolvedTheme | null): void {
  try {
    if (theme) localStorage.setItem(CACHE_KEY, JSON.stringify(theme));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    /* private mode — memory only. The server re-asserts on every load. */
  }
}

/** The device-cached theme. First paint only; never the source of truth. */
export function getCachedTheme(): ResolvedTheme | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as ResolvedTheme) : null;
  } catch {
    return null;
  }
}

/** Paint the cached theme immediately, before any request goes out.
 *  Called from each app's entry point so nobody watches the house default
 *  flash into the studio's own colours on every page load. */
export function initThemeFromCache(): void {
  applyTheme(getCachedTheme());
}

export function applyBrandingToCSS(branding: Branding | null) {
  if (!branding) return;
  const root = document.documentElement;
  root.style.setProperty('--fb-primary', branding.primary_color);
  root.style.setProperty('--fb-secondary', branding.secondary_color);
  root.style.setProperty('--fb-accent', branding.accent_color);

  // The resolved design-token theme rides on the same payload.
  const theme = (branding as Branding & { theme?: ResolvedTheme }).theme;
  if (theme) {
    applyTheme(theme);
    cacheTheme(theme);
  }
}

export const baseCss = `
:root {
  --fb-primary: #1f6feb;
  --fb-secondary: #0a2540;
  --fb-accent: #ff6a3d;
  --fb-bg: #fafafa;
  --fb-text: #111;
  --fb-muted: #6b7280;
  --fb-border: #e5e7eb;
}
* { box-sizing: border-box; }
html, body, #root { height: 100%; margin: 0; font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif; }
body { background: var(--fb-bg); color: var(--fb-text); }
button { cursor: pointer; }
.btn { background: var(--fb-primary); color: white; border: 0; padding: 10px 16px; border-radius: 8px; font-weight: 600; }
.btn:disabled { opacity: .5; cursor: not-allowed; }
.btn-secondary { background: transparent; color: var(--fb-primary); border: 1px solid var(--fb-primary); }
.card { background: white; border: 1px solid var(--fb-border); border-radius: 12px; padding: 16px; }
.muted { color: var(--fb-muted); }
.grid { display: grid; gap: 16px; }
input, select, textarea { padding: 10px 12px; border: 1px solid var(--fb-border); border-radius: 8px; width: 100%; font-size: 14px; }
.h1 { font-size: 28px; font-weight: 700; margin: 0; }
.h2 { font-size: 20px; font-weight: 600; margin: 0; }
.tag { display: inline-block; background: rgba(31,111,235,.1); color: var(--fb-primary); padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; }
`;
